import React, { Children, ReactNode, cloneElement, isValidElement, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

interface IFadeTransitionProps {
	in?: boolean;
	timeout: number;
	children: ReactNode;
}

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
	if (typeof ref === 'function') {
		ref(value);
	} else if (ref != null) {
		(ref as React.MutableRefObject<T | null>).current = value;
	}
}

const FadeTransition: React.FC<IFadeTransitionProps> = (props) => {
	const nodeRef = useRef<HTMLElement | null>(null);

	const child = Children.only(props.children);

	if (!isValidElement(child)) {
		throw new Error('FadeTransition requires a single React element child');
	}

	let transitionChild: React.ReactElement;

	if (typeof child.type === 'string') {
		const previousRef = (child as unknown as { ref?: React.Ref<HTMLElement> }).ref;
		transitionChild = cloneElement(
			child,
			{
				ref: (instance: HTMLElement | null) => {
					nodeRef.current = instance;
					assignRef(previousRef, instance);
				},
			} as { ref?: React.RefCallback<HTMLElement> }
		);
	} else {
		transitionChild = (
			<div ref={nodeRef as React.RefObject<HTMLDivElement>} className="fade-transition-root">
				{child}
			</div>
		);
	}

	return (
		<CSSTransition
			in={props.in}
			nodeRef={nodeRef as React.RefObject<HTMLElement>}
			timeout={props.timeout}
			unmountOnExit={true}
			classNames="fade"
		>
			{transitionChild}
		</CSSTransition>
	);
};

export default React.memo(FadeTransition);
